import { WHITESPACE } from './constants.ts';
import { fontStringParser } from './fontStringParser.ts';
import { measureText } from './measureText.ts';
import { Token, Break, LineBreak, SoftHyphen } from './parser/tokens.ts';
import type { FontProps, LayoutOptions, Line, Lines, NumberFunc, NumberLineFunc } from './types.ts';

const re_whitespace = /[\r\n\xA0]+/g;

function mergeFont (baseFont: FontProps, currentFont: FontProps): FontProps {
  return {
    family: currentFont.family ?? baseFont.family ?? 'sans-serif',
    style: currentFont.style ?? baseFont.style ?? 'normal',
    variant: currentFont.variant ?? baseFont.variant ?? 'normal',
    weight: currentFont.weight ?? baseFont.weight ?? 400,
    height: currentFont.height ?? baseFont.height ?? 14,
    size: currentFont.size ?? baseFont.size ?? 12,
    sizeAdjust: currentFont.sizeAdjust ?? baseFont.sizeAdjust ?? undefined,
    baseline: currentFont.baseline ?? baseFont.baseline ?? 0,
    tracking: currentFont.tracking ?? baseFont.tracking ?? 0,
    href: currentFont.href ?? baseFont.href ?? '',
    color: currentFont.color ?? baseFont.color ?? '',
    rel: currentFont.rel ?? baseFont.rel ?? '',
    target: currentFont.target ?? baseFont.target ?? '',
    class: currentFont.class ?? baseFont.class ?? ''
  };
}

function overflow_line (target_line: Line, target_width: number, token: Token) {
  let line_width = target_line.width;
  let last: Token | undefined;
  let temp: number;
  while (line_width + token.width > target_width && target_line.length) {
    last = target_line[target_line.length - 1];
    temp = last.width ?? 0;
    if (last.width && last.width > token.width) {
      // reduce the token while possible
      last.value = last.value.slice(0, -1);
      last.width = measureText(last, last.font);
      line_width += last.width;
    }
    else {
      // otherwise remove the token
      target_line.pop();
    }
    line_width -= temp;
  }
  if (target_line[target_line.length - 1] instanceof SoftHyphen) {
    // don't add ellipsis directly after hyphen, it is gross
    target_line.pop();
  }

  if (last) {
    // it is possible that the last line is empty, because of LineBreaks
    last = target_line[target_line.length - 1] || last || {};
    token.font = Object.create(last.font);
    token.font.href = target_line.length ? last.font.href : undefined;
    token.font.rel = target_line.length ? last.font.rel : undefined;
    token.font.target = target_line.length ? last.font.target : undefined;
    target_line.push(token);
  }
}

function getEmptyLines (f_base: FontProps): Lines {
  return Object.assign([], {
    height: 0,
    font: f_base,
    width: 0,
    hasOverflow: false,
    hasLineOverflow: false
  });
}

function cloneToken (src: Token): Token {
  const t = new Token(src.value);
  t.width = src.width ?? 0;
  t.whitespace = src.whitespace;
  t.font = src.font;
  return t;
}

type BreakPoint = {
  index: number;
  width: number;
};

/**
 * WARNING: this method is destructive to the tokens
 */
export function linebreak (tokens: Token[], opts: LayoutOptions): Lines {
  const f_base = fontStringParser(opts.font);
  if (!tokens.length) {
    return getEmptyLines(f_base);
  }

  const height: NumberFunc = typeof opts.height === 'function'
    ? opts.height
    : () => opts.height as number;
  const width: NumberLineFunc = typeof opts.width === 'function'
    ? opts.width
    : () => opts.width as number;
  const lineclamp = opts.overflowLine;
  const wrapMode = opts.overflowWrap;

  // fonts
  const f_bold = { ...f_base, weight: 700 };
  const fHeight = f_base.height ?? f_base.size ?? 12;

  const max_lines = isFinite(height())
    ? Math.floor(height() / fHeight)
    : Infinity;

  // skip the work for things what will never show anyway
  if ((!height() && !width(0)) || !max_lines) {
    return getEmptyLines(f_base);
  }

  let index = 0;
  let line_index = 0;
  let line_width = 0;
  const final_breaks: number[] = [];
  let possible_breaks: BreakPoint[] = [];
  let last_was_whitespace = false;

  while (index < tokens.length && line_index < max_lines) {
    const token = tokens[index];
    const font_inst = mergeFont(f_base, token.font); // XXX: don't need this for Break or LineBreak

    token.width = measureText(token, font_inst);
    token.font = font_inst;
    token.line = line_index;
    token.whitespace = token.value in WHITESPACE;

    if (token.value) {
      // normalize whitespace (SVG doesn't "space" \n like HTML does)
      token.value = token.value.replace(re_whitespace, ' ');
    }

    if (!line_width && token.whitespace ||
         last_was_whitespace && token.whitespace) {
      // ignore whitespace at the start of a line
      // ignore repeat whitespace
    }
    else if (token instanceof LineBreak) {
      line_width = 0;
      possible_breaks = [];
      final_breaks.push(index + 1);
      line_index++;
    }
    else if (token instanceof Break || token instanceof SoftHyphen) {
      possible_breaks.push({
        index: index,
        width: line_width
      });
    }
    // have space to add things or this is whitespace
    else if (token.whitespace || line_width + token.width < width(line_index)) {
      line_width += token.width;
    }
    // out of space in the line, need to perform a break
    else if (!possible_breaks.length) {
      // if there are no possible breaks we diverge:
      // - break-word: inject a break and continue
      // - else: add the token which overflows
      if (wrapMode === 'break-word') {
        const lw = width(line_index);
        if ((line_width + token.width) > lw) {
          // split the token into two halves
          const post = cloneToken(token);
          // reduce the token to fit the line
          do {
            token.value = token.value.slice(0, -1);
            token.width = measureText(token, token.font || f_base);
            line_width += token.width;
          }
          while (token.value && token.width > lw);
          // add the remainder and a breakpoint to the stream
          post.value = post.value.slice(token.value.length);
          tokens.splice(index + 1, 0, new Break(), post);
        }
        final_breaks.push(index + 1);
        line_width = 0;
        line_index++;
      }
      else {
        line_width += token.width;
      }
    }
    else {
      let break_rep: BreakPoint;
      let break_accepted: boolean;
      // seek backwards through the breakpoints in the line
      // and determine if any of them are suitable
      do {
        break_accepted = true;
        break_rep = possible_breaks.pop()!;
        const break_token: Token = tokens[break_rep.index];
        let hyp_width: number;
        if (break_token instanceof SoftHyphen) {
          hyp_width = measureText('-', break_token.font || f_base);
          if (break_rep.width + hyp_width > width(line_index)) {
            // won't fit so we'll try again if we have more breaks
            break_accepted = !possible_breaks.length;
          }
        }
      }
      while (!break_accepted);

      // remember this break's index in the tokenstream
      final_breaks.push(break_rep.index + 1);
      line_width = 0;
      line_index++;

      index = break_rep.index;
      possible_breaks = [];
    }

    index++;
    last_was_whitespace = token.whitespace;
  }
  // cut remainder of last line if needed
  if (index !== final_breaks[final_breaks.length - 1]) {
    final_breaks.push(index);
  }

  // convert breakpoints to lines
  let last_break = 0;
  let widest = 0;
  const lines = final_breaks.map(p => {
    // find first token that is not "junk"
    let s = last_break;
    let t: Token;
    while ((t = tokens[s]) && (t.whitespace || !t.value)) {
      // this trims breaks and whitespace from the start of the line
      s++;
    }
    // find last token that is not "junk"
    let e = p;
    let hardbreak: Token | null = null;
    while ((e > s) && (t = tokens[e - 1]) &&
      (t.whitespace || !(t.value || t instanceof SoftHyphen))) {
      // this trims breaks and whitespace from the end of the line
      if (t instanceof LineBreak) {
        // preserve hard breaks so we can justify text later
        hardbreak = t;
      }
      e--;
    }
    // last token in line is a soft-hyphen, which expands to a dash
    if (t instanceof SoftHyphen) {
      t.value = '-';
      t.width = measureText('-', t.font || f_base);
    }
    // next start pos
    last_break = p;
    // cut and clean the line
    const line = Object.assign(
      tokens.slice(s, e).filter(d => d.value),
      { width: 0 }
    );
    if (hardbreak) {
      line.push(hardbreak);
    }
    // keep track of line widths
    line.width = line.reduce((a, d) => a + (d.width || 0), 0);
    if (line.width > widest) {
      widest = line.width;
    }
    return line;
  });

  // per-line overflow
  let hasLineOverflow = false;
  if (lineclamp) {
    const overflow = (lineclamp === 'ellipsis') ? '…' : lineclamp;
    lines.forEach((line, line_index) => {
      const w = width(line_index);
      if (line.width > w) {
        const o_token = new Token(overflow);
        o_token.font = f_base;
        o_token.width = measureText(overflow, f_bold);
        overflow_line(line, w, o_token);
        hasLineOverflow = true;
      }
    });
  }

  // overflow needed?
  let hasOverflow = false;
  const overflow = (opts.overflow === 'ellipsis') ? '…' : opts.overflow;
  if (overflow && index !== tokens.length) {
    const line_width = width(lines.length - 1);
    const last_line = lines[lines.length - 1];
    const o_token = new Token(overflow);
    o_token.font = f_base;
    o_token.width = measureText(overflow, f_bold);
    overflow_line(last_line, line_width, o_token);
    hasOverflow = true;
  }
  else {
    hasOverflow = false;
  }

  return Object.assign(lines, {
    height: lines.length * fHeight,
    font: f_base,
    width: widest,
    hasOverflow,
    hasLineOverflow
  });
}
